import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Share2, Check, Copy } from "lucide-react";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { formatNumber } from "@/shared/utils";
import { cn } from "@/lib/utils.ts";
import { ImageType } from "@/pages/challenges/types/common";
import { P2PUser } from "../api";
import ShareChekSheet from "./ShareChekSheet.tsx";

/* ---- Detail row — "Other cases / Bill" Figma style: muted white/50
   label on the left, white regular value on the right, a thin white/08
   divider below every row except the last. */

function Row({
  label,
  value,
  last = false,
}: {
  label: string;
  value: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 py-3",
        !last && "border-b border-white/08"
      )}
    >
      <p className="text-xs font-normal leading-130 text-white/50 shrink-0">
        {label}:
      </p>
      <p className="text-xs font-normal leading-130 text-white text-end">
        {value}
      </p>
    </div>
  );
}

/* ---- Receipt ---- */

export interface TransferReceiptProps {
  amount: string;
  commissionUzs: string | number;
  transferId: string | null;
  completedAt: Date;
  sender: {
    avatar: ImageType | null;
    fullName: string;
    username: string | null;
    isPremium?: boolean;
  };
  receiver: P2PUser;
  onDone: () => void;
  onSendMore: () => void;
}

export default function TransferReceipt({
  amount,
  commissionUzs,
  transferId,
  completedAt,
  sender,
  receiver,
  onDone,
}: TransferReceiptProps) {
  const { t } = useTranslation();
  // Opens the in-app share Drawer with three explicit action buttons
  // ("Send via Telegram", "Save", "Copy link"). Every direct share API
  // turned out to be broken on Android Telegram WebView, so the pragmatic
  // answer is letting the user pick the channel themselves.
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  const commissionNum =
    typeof commissionUzs === "string"
      ? parseFloat(commissionUzs)
      : commissionUzs;

  // Prefer @username (Telegram-native), fall back to full name.
  const senderLabel = sender.username
    ? `@${sender.username}`
    : sender.fullName || "—";
  const receiverLabel = receiver.username
    ? `@${receiver.username}`
    : receiver.full_name || "—";

  const handleCopyId = async () => {
    if (!transferId) return;
    try {
      await navigator.clipboard.writeText(transferId);
      showCustomToast(
        t("p2p_receipt_id_copied") || "Tranzaksiya ID nusxalandi",
        "success"
      );
    } catch {
      showCustomToast(t("copy_failed") || "Nusxalab bo'lmadi", "error");
    }
  };

  return createPortal(
    <div
      className="fixed inset-x-0 top-0 z-40 flex flex-col overflow-hidden"
      style={{
        height: "min(var(--tg-viewport-height, 100dvh), 100dvh)",
        paddingTop: "var(--safe-area-inset-top, 0px)",
      }}
    >
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-5 px-5">
        <div className="relative size-32 shrink-0">
          <span className="absolute inset-0 rounded-full bg-orange-100/[0.08]" />
          <span className="absolute inset-3 rounded-full bg-orange-100/[0.16]" />
          <span className="absolute inset-7 rounded-full bg-orange-100 border border-white/[0.14] flex items-center justify-center">
            <Check className="size-10 text-white" strokeWidth={2.6} />
          </span>
        </div>
        <div className="flex flex-col items-center gap-0.5 text-center text-white w-full">
          <h2 className="text-[26px] font-bold leading-130">
            {formatNumber(amount)} DON
          </h2>
          <p className="text-xs font-normal leading-130">
            {t("p2p_receipt_success_subtitle")}
          </p>
        </div>
      </div>


      <div
        className="shrink-0 bg-white/08 rounded-t-[28px] px-4 pt-4"
        style={{ paddingBottom: "max(var(--safe-area-inset-bottom, 0px), 16px)" }}
      >
        <div>
          <div className=" bg-black/[0.24] rounded-t-[20px] px-3">
            {transferId && (
              <div className="flex items-center justify-between gap-3 py-3 border-b border-white/08">
                <p className="text-xs font-normal leading-130 text-white/50 shrink-0">
                  {t("transactionId") || "Tranzaksiya ID"}:
                </p>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="flex items-center gap-1.5 min-w-0 active:opacity-60 transition-opacity"
                >
                  <span className="text-xs font-normal leading-130 text-white truncate">
                    {transferId.slice(0, 8)}…
                  </span>
                  <Copy className="size-3.5 text-orange-100 shrink-0" />
                </button>
              </div>
            )}
            <Row
              label={t("p2p_receipt_sender") || "Yuboruvchi"}
              value={senderLabel}
            />
            <Row
              label={t("p2p_receipt_receiver") || "Qabul qiluvchi"}
              value={receiverLabel}
            />
            <Row
              label={t("operation_date") || "Amaliyot bajarilgan sana"}
              value={dayjs(completedAt).format("DD.MM.YYYY, HH:mm")}
            />
            <Row
              label={t("p2p_receipt_commission") || "Komissiya"}
              value={`${formatNumber(commissionNum)} UZS`}
              last
            />
          </div>
          <div className="h-3.5 flex justify-between overflow-hidden">
            {Array.from({ length: 16 }).map((_, i) => (
              <span
                key={i}
                className="size-[18px] -mt-[9px] rounded-full bg-black/[0.24] shrink-0"
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2.5 pt-[max(36px,14vh)]">
          <button
            type="button"
            onClick={() => setIsShareSheetOpen(true)}
            disabled={!transferId}
            className="size-11 shrink-0 rounded-xl border-[1.6px] border-[#F5BA59] text-white flex items-center justify-center active:opacity-70 transition-opacity disabled:opacity-40"
            aria-label={t("p2p_receipt_share")}
          >
            <Share2 className="size-5 text-white" />
          </button>
          <button
            type="button"
            onClick={onDone}
            className="flex-1 h-11 rounded-xl bg-yellow-linear text-[#101017] text-xs font-medium leading-140 flex items-center justify-center active:opacity-90 transition-opacity"
          >
            {t("p2p_done") || "Tayyor"}
          </button>
        </div>
      </div>

      <ShareChekSheet
        isOpen={isShareSheetOpen}
        onClose={() => setIsShareSheetOpen(false)}
        transferId={transferId}
      />
    </div>,
    document.body
  );
}
